import React, { useEffect, useRef, useState } from 'react';
import styles from './MultiSelectSearch.module.css';

export interface SelectOption {
  value: string;
  label: string;
}

interface MultiSelectSearchProps {
  label: string;
  options: SelectOption[];
  selected: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
}

const MultiSelectSearch: React.FC<MultiSelectSearchProps> = ({
  label,
  options,
  selected,
  onChange,
  placeholder = 'Buscar...',
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const count = selected.length;

  return (
    <div className={styles.wrapper} ref={ref}>
      <button
        type="button"
        className={`${styles.trigger} ${count > 0 ? styles.triggerActive : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span>{label}</span>
        <span className={`${styles.countBadge} ${count === 0 ? styles.countHidden : ''}`}>
          {count}
        </span>
        <i className="uc-icon icon-size--sm">{open ? 'expand_less' : 'expand_more'}</i>
      </button>

      {open && (
        <div className={styles.dropdown}>
          {options.length > 4 && (
            <input
              type="text"
              className={styles.search}
              placeholder={placeholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          )}
          <ul className={styles.list}>
            {filtered.length === 0 && (
              <li className={styles.noResults}>Sin resultados</li>
            )}
            {filtered.map((option) => (
              <li key={option.value} className={styles.item}>
                <label className={styles.itemLabel}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={selected.includes(option.value)}
                    onChange={() => toggle(option.value)}
                  />
                  {option.label}
                </label>
              </li>
            ))}
          </ul>
          {count > 0 && (
            <button
              type="button"
              className={styles.clear}
              onClick={() => {
                onChange([]);
                setOpen(false);
              }}
            >
              Limpiar selección
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectSearch;
